<!--
  ZoneTable part — the batch action bar that slides in under the toolbar once rows are selected.
  INTERNAL: not a Zone component, no story, not in llms.txt. Rendered only by Table.vue, whose
  guide documents the behaviour under SELECT and NOTES.

  Presentational: it is told how many rows are selected and what the actions are, and asks for
  things through `clear` / `select-all-across-pages` / `action`. The one piece of state it does own
  is `menuOpen` for the overflow menu — Table.vue closes it on a document scroll through the
  `closeMenu()` method, since Reka would otherwise keep that state to itself.

  The "first 3 inline, the rest under ⋮" split lives here rather than in Table.vue: it is a fact
  about how this bar presents a list of actions, not about the table's data. Three is the cap, not
  a promise: when the bar is too narrow for them (a phone, a sidebar table) actions move under ⋮
  one at a time until nothing overflows, and come back as the bar widens.
-->

<template>
  <!--
    The bar grows into place and pushes the table down rather than appearing on top of it.
    A Vue <Transition>, not the CSS-animation dance in Drawer.vue: nothing here sits behind
    Reka's `Presence`, so Vue owns the mount/unmount timing and a transition is never cut off
    mid-flight. The bar is a known 40px tall, so `max-height` animates from 0 with no measuring;
    it is in normal document flow, so the table below is displaced by exactly as much.

    The shell exists to own `overflow-hidden` during the sweep. Without it the bar's own
    `border-b` would still paint at the closed extreme — a 1px line flashing under the toolbar
    on every enter and leave. `motion-reduce:transition-none` snaps for anyone who asked for
    less motion.
  -->
  <Transition
    enter-active-class="overflow-hidden transition-[max-height,opacity] duration-200 ease-out motion-reduce:transition-none"
    enter-from-class="max-h-0 opacity-0"
    enter-to-class="max-h-[40px] opacity-100"
    leave-active-class="overflow-hidden transition-[max-height,opacity] duration-150 ease-in motion-reduce:transition-none"
    leave-from-class="max-h-[40px] opacity-100"
    leave-to-class="max-h-0 opacity-0"
  >
    <div
      v-if="visible"
      data-testid="batch-bar-shell"
    >
      <!--
        `whitespace-nowrap` + `shrink-0` on every child: the fit below reads "does not fit" as the
        bar's scrollWidth outgrowing its clientWidth, which only happens when the children refuse
        to wrap or squeeze. Without them flex would shrink "Select all (8)" onto two lines instead.
      -->
      <div
        ref="barEl"
        class="flex h-10 items-center gap-3 whitespace-nowrap border-b border-gray-200 bg-primary-700 px-3.5 text-sm text-white"
        data-testid="batch-bar"
      >
        <!-- Light blue, not ZonePill's gray default: gray on the primary-700 bar reads as a
             disabled chip. `primary` is bg-primary-100 with primary-800 text. -->
        <ZonePill
          color="primary"
          size="md"
          dismissible
          class="shrink-0"
          data-testid="batch-selected"
          @dismiss="$emit('clear')"
        >
          {{ t('selected', { selected: selectedCountText }) }}
        </ZonePill>

        <ZoneButton
          v-if="canSelectAllAcrossPages"
          variant="ghost"
          color="white"
          size="sm"
          class="shrink-0"
          data-testid="batch-select-all"
          @click="$emit('select-all-across-pages')"
        >
          {{ t('select_all_total', { total: batchTotalText }) }}
        </ZoneButton>

        <div class="ml-auto flex shrink-0 items-center gap-2.5">
          <ZoneButton
            v-for="action in inlineBatchActions"
            :key="action.id"
            variant="ghost"
            color="white"
            size="sm"
            :disabled="action.disabled"
            :leading-icon="action.icon || undefined"
            :data-testid="`batch-action-${action.id}`"
            @click="$emit('action', action)"
          >
            {{ action.label }}
          </ZoneButton>

          <!--
            Deliberately NOT ZoneButtonDropdown: that component owns its open state internally with
            no `open` prop, and this menu has to close on the same document scroll as the filters
            panel (see onDocumentScroll). The trigger and the menu still borrow ZoneButtonDropdown's
            exact ZoneButton + Figma item styling so the two read as one control.
          -->
          <DropdownMenuRoot
            v-if="overflowBatchActions.length"
            v-model:open="menuOpen"
          >
            <DropdownMenuTrigger as-child>
              <ZoneButton
                icon-only
                variant="ghost"
                color="white"
                size="sm"
                leading-icon="more_vert"
                :aria-label="t('more')"
                data-testid="batch-overflow"
                :class="menuOpen ? 'ring-2 ring-white' : undefined"
              />
            </DropdownMenuTrigger>
            <DropdownMenuPortal to="#zone-teleport">
              <DropdownMenuContent
                align="end"
                :side-offset="4"
                class="z-50 min-w-[200px] overflow-hidden rounded-md border border-gray-300 bg-white shadow-md focus:outline-none"
              >
                <DropdownMenuItem
                  v-for="action in overflowBatchActions"
                  :key="action.id"
                  :disabled="action.disabled"
                  class="group flex cursor-pointer select-none items-center gap-2 border-b border-gray-200 px-4 py-3 text-sm text-gray-900 outline-none data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50 data-[highlighted]:bg-indigo-50"
                  @select="$emit('action', action)"
                >
                  <ZoneIcon
                    v-if="action.icon"
                    :icon="action.icon"
                    size="lg"
                    class="text-gray-600 group-data-[highlighted]:text-primary-700"
                  />
                  <span class="flex-1 truncate">{{ action.label }}</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenuPortal>
          </DropdownMenuRoot>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, watch, type PropType } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  DropdownMenuRoot,
  DropdownMenuTrigger,
  DropdownMenuPortal,
  DropdownMenuContent,
  DropdownMenuItem,
} from 'reka-ui';
import ZoneIcon from '../Icon.vue';
import ZoneButton from '../Button.vue';
import ZonePill from '../Pill.vue';
import type { TableBatchAction } from './types';

// The batch bar shows at most this many actions inline; everything past it moves under the "more"
// menu. Ported from the Zone spec ("Max 10 actions ... Only 3 will be displayed"). Fewer show when
// the bar has no room for them — see fitInlineActions.
const INLINE_BATCH_ACTIONS = 3;
</script>

<script setup lang="ts">
defineOptions({ name: 'ZoneTableBatchBar' });

const { t } = useI18n();

const props = defineProps({
  /** Drives the enter/leave transition, so the bar animates rather than appearing. */
  visible: {
    type: Boolean,
    required: true,
  },
  /** Already formatted, so this bar and the pagination summary group numbers identically. */
  selectedCountText: {
    type: String,
    required: true,
  },
  batchActions: {
    type: Array as PropType<TableBatchAction[]>,
    required: true,
  },
  /** The "Select all (N)" affordance, which is redundant under cross-page selection. */
  canSelectAllAcrossPages: {
    type: Boolean,
    required: true,
  },
  batchTotalText: {
    type: String,
    required: true,
  },
});

defineEmits(['clear', 'select-all-across-pages', 'action']);

// Controlled so Table.vue can close it on a document scroll; Reka would otherwise own this
// internally and there would be no way in.
const menuOpen = ref(false);

const barEl = ref<HTMLElement | null>(null);

// How many actions are inline right now: the cap, minus whatever the bar cannot fit.
const inlineCount = ref<number>(INLINE_BATCH_ACTIONS);

const inlineBatchActions = computed<TableBatchAction[]>(() => props.batchActions.slice(0, inlineCount.value));

const overflowBatchActions = computed<TableBatchAction[]>(() => props.batchActions.slice(inlineCount.value));

let resizeObserver: ResizeObserver | null = null;

// Bumped by every fit so a run that is still stepping through nextTicks stops when a newer one
// starts; two runs decrementing the same counter would otherwise hand away actions that fit.
let fitGeneration = 0;

/**
 * Start from the cap and hand actions to the ⋮ menu one at a time until the bar stops
 * overflowing. Overflow is read off the DOM (scrollWidth past clientWidth) rather than from
 * summed button widths, so icons, labels and the Select-all link never need measuring on their
 * own. Every step is a nextTick, and the ResizeObserver callback that triggers this runs before
 * paint, so the intermediate layouts are never seen.
 */
async function fitInlineActions() {
  fitGeneration += 1;

  const generation = fitGeneration;

  inlineCount.value = INLINE_BATCH_ACTIONS;

  const bar = barEl.value;

  if (!bar) {
    return;
  }

  // Icons are font ligatures: until the symbols font is in, "more_vert" is a word wider than its
  // icon box, every button measures too wide and the bar would shed actions it can fit. Resolves
  // at once when nothing is loading; jsdom has no `document.fonts`.
  await document.fonts?.ready;
  await nextTick();

  while (inlineCount.value > 0 && bar.scrollWidth > bar.clientWidth) {
    if (generation !== fitGeneration) {
      return;
    }

    inlineCount.value -= 1;
    await nextTick();
  }
}

// The bar is `v-if`'d on `visible`, so it is observed per mount: the observer fires once on
// `observe()` (the initial fit) and again on every width change. jsdom has no ResizeObserver, and
// there the cap simply stands.
watch(barEl, (bar) => {
  resizeObserver?.disconnect();
  resizeObserver = null;

  if (!bar || typeof ResizeObserver === 'undefined') {
    return;
  }

  resizeObserver = new ResizeObserver(() => {
    fitInlineActions();
  });
  resizeObserver.observe(bar);
}, { flush: 'post' });

// Keyed on what changes the buttons' widths, not on the array: callers write the actions as an
// inline literal, which is a new array on every parent render.
watch(() => props.batchActions.map((action) => `${action.id}:${action.label}:${action.icon ?? ''}`).join('\n'), () => {
  fitInlineActions();
});

onBeforeUnmount(() => {
  resizeObserver?.disconnect();
});

/** Called by Table.vue's document-scroll handler — see onDocumentScroll there. */
function closeMenu() {
  menuOpen.value = false;
}

// Only the SHELL is `v-if`'d on `visible` — BatchBar itself stays mounted (Table.vue renders
// it unconditionally and drives this prop), so `menuOpen` outlives the bar. Left set, the
// dropdown remounts already-open the next time a selection brings the bar back, popping a
// menu nobody asked for. Watch the false edge only: reopening is the user's business, and
// resetting on the true edge would fight `closeMenu`'s contract with Table.vue.
watch(() => props.visible, (isVisible) => {
  if (!isVisible) {
    closeMenu();
  }
});

defineExpose({ menuOpen, closeMenu });
</script>

<i18n lang="json">
{
  "en": {
    "selected": "{selected} selected",
    "select_all_total": "Select all ({total})",
    "more": "More actions"
  }
}
</i18n>
